import { useState, useMemo, useEffect, useRef } from "react";
import html2pdf from "html2pdf.js";
import { can, machineTypeKey, SC, SC_OVERDUE, SL, FLOORS, getMonthsForFY, pmCellStatus, pmCellMeta, getDuePMs, isPMCellOverdue, monthKeyToDate, currentMonthKey, COMBINED_SHEET_FREQ, buildCombinedCheckSheet, sheetFrequencyOptions } from "../config";
import { apiCall, API, getAuthHeaders } from "../api";
import axios from "axios";
import { Modal } from "./common/Modal";
import PMCheckSheetFillModal from "./PMCheckSheetFillModal";
import { FileUploadBox, TimeField } from "./common/FormElements";

function escapeHtml(str) {
  if (str == null) return "";
  return String(str).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;").replace(/'/g,"&#39;");
}

// Lowercase, "*" -> "x", then drop EVERY separator (dash, slash, dot, space).
// The same machine is spelled inconsistently across the PM Plan and the Machine List
// ("US/DG320-1/6" vs "US-DG320-1/6", "PWM - 1" vs "PWM-1"). Stripping only dashes left
// those plans unlinked, so the modal could not resolve the machine type and skipped the
// check sheet. Mirror of _normalize_machine_code() in backend/core/database.py.
function normalizeMachineCode(code) {
  if (!code) return "";
  return String(code).trim().toLowerCase().replace(/\*/g, "x").replace(/[^a-z0-9]/g, "");
}

// A machineId stringified from a null upstream — treat as "no id" and fall back to code.
const MISSING_MACHINE_IDS = new Set(["", "none", "null", "nan", "undefined"]);

function findMachineForPlan(machines, plan) {
  if (!plan || !machines) return null;
  // Prefer plan.machineId (stable UUID) over code text matching
  if (plan.machineId && !MISSING_MACHINE_IDS.has(String(plan.machineId).toLowerCase())) {
    const byId = machines.find(m => m.id === plan.machineId);
    if (byId) return byId;
  }
  const planNorm = normalizeMachineCode(plan.machineCode);
  if (!planNorm) return null;
  return machines.find(m => normalizeMachineCode(m.machineNo) === planNorm) || null;
}

const FY_MONTH_POS = {APR:1,MAY:2,JUN:3,JUL:4,AUG:5,SEP:6,OCT:7,NOV:8,DEC:9,JAN:10,FEB:11,MAR:12};
function validateScheduleFreq(freq, monthsList) {
  const f = (freq || "").toUpperCase().trim();
  const months = monthsList || [];
  if (f.includes("NO SCHEDULE")) {
    if (months.length !== 0) return { ok: false, error: "NO SCHEDULE frequency cannot have any scheduled months. Please uncheck all months." };
    return { ok: true };
  }
  if (f.includes("1 YEAR")) {
    if (months.length === 0) return { ok: false, error: "1 YEAR frequency requires exactly 1 scheduled month. Please check 1 month." };
    if (months.length > 1) return { ok: false, error: "1 YEAR frequency allows only 1 scheduled month. Please uncheck all but one." };
    return { ok: true };
  }
  if (f.includes("6 MONTH")) {
    if (months.length !== 2) return { ok: false, error: `6 MONTH frequency requires exactly 2 scheduled months 6 months apart (currently ${months.length} selected).` };
    const positions = months.map(m => FY_MONTH_POS[m.split("-")[0].toUpperCase()] || 0).sort((a,b) => a - b);
    if (positions[1] - positions[0] !== 6) return { ok: false, error: "6 MONTH months must be exactly 6 months apart. Valid pairs: APR+OCT, MAY+NOV, JUN+DEC, JUL+JAN, AUG+FEB, SEP+MAR." };
    return { ok: true };
  }
  return { ok: true };
}

function PMEditModal({ plan, fyMonths, floorOptions, locationOptions, unitFloors, onSave, onClose }) {
  const [desc, setDesc] = useState(plan.desc || "");
  const [location, setLocation] = useState(plan.location || "");
  const [floor, setFloor] = useState(plan.floor || "");
  const [freq, setFreq] = useState(plan.freq || "");
  const initialMonths = Object.keys(plan.schedule || {});
  const [selectedMonths, setSelectedMonths] = useState(new Set(initialMonths));
  const [saving, setSaving] = useState(false);

  const finalFloorOptions = useMemo(() => {
    // Kept in step with the machine form by reading the shared list.
    const STANDARD = unitFloors && unitFloors.length ? unitFloors : FLOORS;
    const ORDER = Object.fromEntries(STANDARD.map((f, i) => [f, i]));
    const set = new Set(STANDARD);
    (floorOptions || []).forEach(f => { if (f) set.add(f); });
    if (floor) set.add(floor);
    return [...set].filter(Boolean).sort((a, b) => {
      const ra = a in ORDER ? ORDER[a] : 999;
      const rb = b in ORDER ? ORDER[b] : 999;
      return ra === rb ? a.localeCompare(b) : ra - rb;
    });
  }, [floorOptions, floor, unitFloors]);

  const finalLocationOptions = useMemo(() => {
    const set = new Set(locationOptions || []);
    if (location) set.add(location);
    return [...set].filter(Boolean).sort();
  }, [locationOptions, location]);

  function toggleMonth(m) {
    setSelectedMonths(prev => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });
  }

  function handleSave() {
    const monthsArr = Array.from(selectedMonths);
    const validation = validateScheduleFreq(freq, monthsArr);
    if (!validation.ok) {
      alert(validation.error);
      return;
    }
    if (!window.confirm("Save these edits?\n\nThis row will be marked as manually edited. Future Re-Generates will not overwrite it.")) return;
    setSaving(true);
    Promise.resolve(onSave({
      desc, location, floor, freq,
      schedule_months: monthsArr,
    })).finally(() => setSaving(false));
  }

  return (
    <Modal onClose={onClose} title={`Edit PM Plan — ${plan.machineCode || ""}`}>
      <div className="space-y-3 p-3">
        <div className="text-xs text-gray-500 bg-yellow-50 border border-yellow-200 rounded p-2">
          ⚠ Machine Code is locked. To change it, edit the machine in Machine List.
        </div>
        <div>
          <label className="text-xs text-gray-500">Machine Code (locked)</label>
          <input value={plan.machineCode || ""} disabled className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Description</label>
          <input value={desc} onChange={e => setDesc(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm" />
        </div>
        <div>
          <label className="text-xs text-gray-500">Location</label>
          <select value={location} onChange={e => setLocation(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white" data-testid="edit-pm-location">
            {finalLocationOptions.length === 0 && <option value="">-- No locations available --</option>}
            {finalLocationOptions.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Floor</label>
          <select value={floor} onChange={e => setFloor(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white" data-testid="edit-pm-floor">
            {finalFloorOptions.length === 0 && <option value="">-- No floors available --</option>}
            {finalFloorOptions.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Frequency</label>
          <select value={freq} onChange={e => setFreq(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm bg-white">
            <option value="6 MONTH">6 MONTH</option>
            <option value="1 YEAR">1 YEAR</option>
            <option value="NO SCHEDULE">NO SCHEDULE</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500">Schedule months</label>
          <div className="grid grid-cols-4 gap-2 mt-2">
            {fyMonths.map(m => (
              <label key={m} className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={selectedMonths.has(m)} onChange={() => toggleMonth(m)} />
                <span className={selectedMonths.has(m) ? "font-semibold" : ""}>{m}</span>
              </label>
            ))}
          </div>
          <div className="text-[10px] text-gray-400 mt-1">Unchecking removes the PM (including any DONE/DELAYED status). Checking adds as PLAN.</div>
        </div>
        <div className="flex gap-2 pt-2">
          <button onClick={handleSave} disabled={saving} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving..." : "Save"}</button>
          <button onClick={onClose} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

function PMStatusModal({ plan, month, currentEntry, machines, pmCheckSheetTemplates, onSave, onClose, user }) {
  const curStatus = pmCellStatus(currentEntry) || "PLAN";
  const curMeta = pmCellMeta(currentEntry);
  const [status, setStatus] = useState(curStatus);
  const [completedDate, setCompletedDate] = useState(curMeta.completedDate || new Date().toISOString().split("T")[0]);
  const [completedBy, setCompletedBy] = useState(curMeta.completedBy || "");
  const [remarks, setRemarks] = useState(curMeta.remarks || "");
  const [evidenceFiles, setEvidenceFiles] = useState(() => {
    if (Array.isArray(curMeta.evidenceAttachments) && curMeta.evidenceAttachments.length) {
      return curMeta.evidenceAttachments.map(a => ({ dataUrl: null, name: a.fileName || "evidence", photoKey: a.photoKey, existing: true }));
    }
    if (curMeta.evidencePhotoKey) {
      return [{ dataUrl: null, name: curMeta.evidenceFileName || "evidence", photoKey: curMeta.evidencePhotoKey, existing: true }];
    }
    if (curMeta.evidenceFile) {
      return [{ dataUrl: curMeta.evidenceFile, name: curMeta.evidenceFileName || "evidence" }];
    }
    return [];
  });
  const [checkSheetData, setCheckSheetData] = useState((currentEntry && typeof currentEntry === "object" && currentEntry.checkSheetData) || null);
  // Which check sheet to fill. "" follows the automatic choice below; re-opening a cell
  // that already has a filled sheet starts on that sheet's frequency.
  const [sheetFreq, setSheetFreq] = useState(
    (currentEntry && typeof currentEntry === "object" && currentEntry.checkSheetData && currentEntry.checkSheetData.frequency) || ""
  );
  const [showCheckSheetForm, setShowCheckSheetForm] = useState(false);
  const [saving, setSaving] = useState(false);

  // Internal / External hybrid state. Default to Internal (95% of cases) so existing behaviour is preserved.
  const [workType, setWorkType] = useState((curMeta.workType === "external") ? "external" : "internal");
  const [vendorName, setVendorName] = useState(curMeta.vendorName || "");
  const [poNumber, setPoNumber] = useState(curMeta.poNumber || "");
  const [poDate, setPoDate] = useState(curMeta.poDate || "");
  const [poAmount, setPoAmount] = useState(curMeta.poAmount || "");
  const [poFilePath, setPoFilePath] = useState(curMeta.poFilePath || "");
  const [poFileName, setPoFileName] = useState(curMeta.poFileName || "");
  // Cash has no purchase order, so its number, date and document are not asked for.
  // The amount stays in poAmount either way, so every total picks cash up too.
  const [paymentMode, setPaymentMode] = useState(curMeta.paymentMode === "cash" ? "cash" : "po");
  // Internal-only time tracking. Default startDate / endDate to the existing completedDate
  // (or today) so reopening a legacy cell keeps a sensible value. startTime / endTime stay empty
  // for legacy cells; user must fill before Save can succeed.
  const [internalStartDate, setInternalStartDate] = useState(curMeta.startDate || curMeta.completedDate || new Date().toISOString().split("T")[0]);
  const [internalStartTime, setInternalStartTime] = useState(curMeta.startTime || "");
  const [internalEndDate, setInternalEndDate] = useState(curMeta.endDate || curMeta.completedDate || new Date().toISOString().split("T")[0]);
  const [internalEndTime, setInternalEndTime] = useState(curMeta.endTime || "");

  const isExternal = workType === "external";
  const paidInCash = paymentMode === "cash";

  // Look up matching template for this plan's machine + freq.
  //
  // Falls back to the plan row's own description when no machine matches it. A plan row
  // whose code does not find its machine — "05" against a machine called
  // "TAPPING MACHINE -05" — still names the machine in its description, and the template
  // is keyed by type, not by which machine it is. Without this the fitter was told no
  // check sheet was required when one plainly was.
  const matchedMachine = findMachineForPlan(machines || [], plan);
  const machineType = matchedMachine?.name || plan.desc || "";

  // Auto-detect 1st vs 2nd PM half from month string ("JUL-26" -> month abbr -> half)
  const monthAbbr = (month || "").split("-")[0].toUpperCase();
  const FIRST_HALF = ["APR", "MAY", "JUN", "JUL", "AUG", "SEP"];
  const pmHalf = (plan.freq === "6 MONTH") ? (FIRST_HALF.includes(monthAbbr) ? 1 : 2) : null;

  // 2nd PM of a 6 MONTH machine uses the 1 YEAR check sheet; 1st PM uses 6 MONTH as normal.
  const _autoFreq = (plan.freq === "6 MONTH" && pmHalf === 2) ? "1 YEAR" : plan.freq;
  // Every sheet published for this machine type — a machine usually has both a 6 MONTH
  // and a 1 YEAR sheet, and the technician can pick whichever the job actually calls for.
  // Compared with whitespace collapsed: the register has machines called
  // "TAPPING  MACHINE" with two spaces, and an exact match never found their
  // "TAPPING MACHINE" template — a check sheet that existed and was never offered.
  const typeKey = machineTypeKey;
  const machineTemplates = (pmCheckSheetTemplates || [])
    .filter(t => typeKey(t.machineType) === typeKey(machineType));
  // Published sheets, plus "6 MONTH + 1 YEAR" when both exist — one visit that covers
  // both services fills a single sheet instead of two.
  const sheetFreqOptions = sheetFrequencyOptions(machineTemplates);
  // Fall back to whatever sheet exists — e.g. a 6-month machine's 2nd PM where no
  // 1 YEAR sheet has been published — so the check sheet never silently disappears.
  const _defaultFreq = sheetFreqOptions.includes(_autoFreq) ? _autoFreq : (sheetFreqOptions[0] || "");
  const effectiveSheetFreq = sheetFreqOptions.includes(sheetFreq) ? sheetFreq : _defaultFreq;
  const matchedTemplate = effectiveSheetFreq === COMBINED_SHEET_FREQ
    ? buildCombinedCheckSheet(machineTemplates)
    : (machineTemplates.find(t => t.frequency === effectiveSheetFreq) || null);
  const templateRequired = !!matchedTemplate;
  // Only pre-fill from a saved sheet when it belongs to the sheet now on screen; the two
  // frequencies have different checkpoints, so rows would not line up. Sheets saved before
  // the frequency was recorded carry none, and are still restored.
  const savedSheetFitsTemplate = !checkSheetData?.frequency || checkSheetData.frequency === effectiveSheetFreq;

  // Called from inside the check sheet, which asks for confirmation first. The saved sheet
  // belongs to the old frequency and its rows cannot carry over, so it is dropped.
  function changeSheetFreq(next) {
    if (next === effectiveSheetFreq) return;
    if (checkSheetData && checkSheetData.frequency !== next) setCheckSheetData(null);
    setSheetFreq(next);
  }

  function addEvidenceFiles(fileList) {
    const files = Array.from(fileList || []);
    const MAX_FILES = 5;
    const MAX_MB = 5;
    const remaining = MAX_FILES - evidenceFiles.length;
    if (remaining <= 0) {
      alert(`Maximum ${MAX_FILES} evidence files allowed.`);
      return;
    }
    const candidates = files.slice(0, remaining);
    const oversized = candidates.filter(f => f.size > MAX_MB * 1024 * 1024);
    if (oversized.length) {
      alert(`${oversized.length} file(s) exceed ${MAX_MB} MB and were skipped.`);
    }
    candidates.filter(f => f.size <= MAX_MB * 1024 * 1024).forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => setEvidenceFiles(prev => [...prev, { dataUrl: ev.target.result, name: file.name }]);
      reader.readAsDataURL(file);
    });
  }

  function removeEvidenceFile(idx) {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== idx));
  }

  function handleSave() {
    if (status === "DONE") {
      if (!evidenceFiles.length) { alert("Please upload at least one work report / evidence file before marking PM as done."); return; }
      // Internal activity fields are required for BOTH Internal and External.
      // External is a hybrid: the internal PM activity still happens; the vendor/PO box only
      // records the outside commercial spend on top. So nothing internal is waived for External.
      if (!completedBy) { alert("Please enter who completed this PM."); return; }
      if (!internalStartDate) { alert("Please enter the Start Date."); return; }
      if (!internalStartTime) { alert("Please enter the Start Time."); return; }
      if (!internalEndDate) { alert("Please enter the End Date."); return; }
      if (!internalEndTime) { alert("Please enter the End Time."); return; }
      // Sanity check: end must not be before start.
      const _startStamp = new Date(`${internalStartDate}T${internalStartTime}`);
      const _endStamp = new Date(`${internalEndDate}T${internalEndTime}`);
      if (!isNaN(_startStamp) && !isNaN(_endStamp) && _endStamp < _startStamp) {
        alert("End Date/Time must be on or after Start Date/Time."); return;
      }
      if (templateRequired && !checkSheetData) { alert("Please fill the Preventive Maintenance Check Sheet before marking PM as done."); return; }
      // External adds the outside commercial spend (Vendor + PO) on top of the internal activity.
      if (isExternal) {
        if (!vendorName.trim()) { alert("Please enter the Vendor Name."); return; }
        if (!paidInCash) {
          if (!poNumber.trim()) { alert("Please enter the PO Number."); return; }
          if (!poDate.trim()) { alert("Please enter the PO Date."); return; }
          if (!poFilePath) { alert("Please upload the PO Document (PDF / image)."); return; }
        }
        if (!String(poAmount).trim() || Number(poAmount) <= 0) { alert(`Please enter a valid ${paidInCash ? "Cash" : "PO"} Amount.`); return; }
      }
    }
    if ((status === "DELAYED" || status === "CANCELLED") && !remarks) { alert("Please enter a reason."); return; }
    setSaving(true);
    const payload = {
      status,
      completedDate,
      completedBy,
      remarks,
      evidenceFiles: evidenceFiles.map(f => f.photoKey ? { photoKey: f.photoKey, fileName: f.name } : { dataUrl: f.dataUrl, fileName: f.name }),
      checkSheetData,
    };
    if (status === "DONE") {
      payload.workType = isExternal ? "external" : "internal";
      // Internal activity fields are saved for BOTH Internal and External (hybrid model).
      // completedBy and checkSheetData are already on the base payload above; we keep them.
      payload.startDate = internalStartDate;
      payload.startTime = internalStartTime;
      payload.endDate = internalEndDate;
      payload.endTime = internalEndTime;
      payload.completedDate = internalEndDate;
      if (isExternal) {
        // External only ADDS the outside commercial spend on top.
        payload.vendorName = vendorName.trim();
        payload.poNumber = poNumber.trim();
        payload.poDate = poDate.trim();
        payload.poAmount = poAmount;
        payload.paymentMode = paymentMode;
        payload.poFilePath = poFilePath;
        payload.poFileName = poFileName;
      }
    }
    Promise.resolve(onSave(payload)).finally(() => setSaving(false));
  }

  return (
    <Modal title={`Update PM Status \u2014 ${plan.desc}`} onClose={onClose}>
      <p className="text-xs text-gray-500 mb-4">{plan.machineCode} &middot; {plan.location} &middot; {month}{plan.freq === "6 MONTH" && pmHalf ? ` \u2014 ${pmHalf === 1 ? "1st PM" : "2nd PM"} of FY` : ""}</p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="text-xs text-gray-500 font-medium">PM Status</label>
          <div className="flex gap-2 mt-1" data-testid="pm-status-options">
            {["PLAN","DONE","DELAYED","CANCELLED"].map(s => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`pm-status-${s.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${status === s ? SC[s] + " border-current" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
                {SL[s]}
              </button>
            ))}
          </div>
        </div>
        {status === "DONE" && (
          <div className={`col-span-2 space-y-3 rounded-xl p-3 border ${isExternal ? "bg-orange-50 border-orange-200" : "bg-green-50 border-green-200"}`}>
            <p className={`text-xs font-semibold ${isExternal ? "text-orange-700" : "text-green-700"}`}>Completion Evidence</p>

            {/* Work Type toggle */}
            <div>
              <label className="text-xs text-gray-500">Work Type</label>
              <div className="flex gap-2 mt-1" data-testid="pm-work-type-options">
                {[["internal","Internal","bg-blue-100 text-blue-800 border-blue-300"],["external","Internal + External","bg-orange-100 text-orange-800 border-orange-300"]].map(([val, label, activeCls]) => (
                  <button key={val} type="button" onClick={() => setWorkType(val)} data-testid={`pm-work-type-${val}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${workType === val ? activeCls : "border-gray-200 text-gray-500 bg-white hover:bg-gray-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Internal activity fields — shown for BOTH Internal and External (hybrid model). */}
            <div className="space-y-3">
              <div><label className="text-xs text-gray-500">Completed By (Internal Technician) *</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="Internal technician name" value={completedBy} onChange={e => setCompletedBy(e.target.value)} data-testid="pm-completed-by" /></div>
              {isExternal && (
                <div><label className="text-xs text-gray-500">Vendor Name / Technician *</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="e.g. ABC Maintenance Services" value={vendorName} onChange={e => setVendorName(e.target.value)} data-testid="pm-vendor-name" /></div>
              )}
              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase">Start of PM</p>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <div><label className="text-xs text-gray-500">Date *</label><input type="date" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" value={internalStartDate} onChange={e => setInternalStartDate(e.target.value)} data-testid="pm-start-date" /></div>
                  <div><label className="text-xs text-gray-500">Time *</label><TimeField value={internalStartTime} onChange={v => setInternalStartTime(v)} testId="pm-start-time" /></div>
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold text-gray-500 uppercase">End of PM</p>
                <div className="grid grid-cols-2 gap-3 mt-1">
                  <div><label className="text-xs text-gray-500">Date *</label><input type="date" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" value={internalEndDate} onChange={e => setInternalEndDate(e.target.value)} data-testid="pm-end-date" /></div>
                  <div><label className="text-xs text-gray-500">Time *</label><TimeField value={internalEndTime} onChange={v => setInternalEndTime(v)} testId="pm-end-time" /></div>
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs text-gray-500">Upload Work Report / Evidence <span className="text-red-500">*</span></label>
              <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1 bg-white" data-testid="pm-evidence-file"
                onChange={e => { addEvidenceFiles(e.target.files); e.target.value = ""; }} />
              {evidenceFiles.length > 0 && (
                <ul className="mt-1 space-y-1">
                  {evidenceFiles.map((f, i) => (
                    <li key={i} className="flex items-center justify-between text-xs bg-white border rounded px-2 py-1">
                      <span className="text-green-700 truncate">{f.name}{f.existing ? " (uploaded)" : ""}</span>
                      <button type="button" onClick={() => removeEvidenceFile(i)} className="text-red-500 hover:text-red-700 font-bold ml-2" title="Remove" data-testid={`pm-evidence-remove-${i}`}>✕</button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-gray-400 mt-1">PDF, JPG or PNG — up to 5 files, 5 MB each.</p>
            </div>

            {isExternal && (
              <div className="space-y-3 bg-white rounded-lg p-3 border border-orange-200">
                <p className="text-xs font-semibold text-orange-700 uppercase">Vendor / Commercial Details</p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Paid by</span>
                  {[["po", "Purchase Order"], ["cash", "Cash"]].map(([val, label]) => (
                    <button key={val} type="button" onClick={() => setPaymentMode(val)}
                      className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-all ${paymentMode === val ? "bg-white text-gray-900 border-gray-400 shadow-sm" : "border-transparent text-gray-500 hover:bg-white/60"}`}
                      data-testid={`pm-payment-mode-${val}`}>{label}</button>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {!paidInCash && <div><label className="text-xs text-gray-500">PO Number *</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="e.g. PO/2026/0123" value={poNumber} onChange={e => setPoNumber(e.target.value)} data-testid="pm-po-number" /></div>}
                  {!paidInCash && <div><label className="text-xs text-gray-500">PO Date *</label><input type="date" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" value={poDate} onChange={e => setPoDate(e.target.value)} data-testid="pm-po-date" /></div>}
                  <div className="col-span-2"><label className="text-xs text-gray-500">{paidInCash ? "Cash" : "PO"} Amount (₹) *</label><input type="number" min="0" step="0.01" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="e.g. 5000" value={poAmount} onChange={e => setPoAmount(e.target.value)} data-testid="pm-po-amount" /></div>
                </div>
                <div>
                  <FileUploadBox
                    label={paidInCash ? "Cash Bill / Receipt (optional)" : "PO Document *"}
                    file={poFilePath || null}
                    fileName={poFileName || ""}
                    onChange={f => { setPoFilePath(f?.path || ""); setPoFileName(f?.name || ""); }}
                    accept=".pdf,.jpg,.jpeg,.png"
                    hint={paidInCash ? "Attach the cash bill or receipt if you have one" : "PDF or image of the Purchase Order"}
                  />
                </div>
              </div>
            )}

            {templateRequired ? (
              <div>
                <label className="text-xs text-gray-500">Preventive Maintenance Check Sheet <span className="text-red-500">*</span></label>
                <div className="mt-1 flex items-center gap-2">
                  <button onClick={() => setShowCheckSheetForm(true)} className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${checkSheetData ? "bg-green-600 text-white hover:bg-green-700" : "bg-blue-600 text-white hover:bg-blue-700"}`} data-testid="pm-fill-checksheet-btn">
                    {checkSheetData ? `\u2713 Filled (${(checkSheetData.results || []).length}/${(matchedTemplate.checkpoints || []).length} checkpoints) \u2014 Edit` : "Fill Preventive Maintenance Check Sheet"}
                  </button>
                  {checkSheetData && <button onClick={() => setCheckSheetData(null)} className="text-xs text-red-500 hover:underline">Clear</button>}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {/* The combined sheet's doc number already spells out both periods, so the
                      trailing "(6 MONTH + 1 YEAR)" would just repeat them. */}
                  Template: {matchedTemplate.docNo || matchedTemplate.machineType}
                  {!matchedTemplate.docNoParts && ` (${matchedTemplate.frequency})`}
                  {sheetFreqOptions.length > 1 && <> &middot; change the frequency inside the check sheet</>}
                  {effectiveSheetFreq !== _autoFreq && <span className="text-amber-700"> &middot; changed from {_autoFreq}</span>}
                </p>
              </div>
            ) : (
              <div className="text-xs text-gray-500 italic">
                {machineType ? `No PM check sheet template found for "${machineType}" (${plan.freq}). One can be added under Machine List \u2192 PM Check Sheets.` : `Machine type unknown for this plan; check sheet not required.`}
              </div>
            )}

            <div><label className="text-xs text-gray-500">Remarks</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder={isExternal ? "e.g. Annual contract maintenance" : "e.g. Oil changed, belts replaced"} value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="pm-remarks" /></div>
          </div>
        )}
        {status === "DELAYED" && (
          <div className="col-span-2 space-y-3 bg-yellow-50 border border-yellow-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-yellow-700">Delay Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-500">Actual Completion Date *</label><input type="date" className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" value={completedDate} onChange={e => setCompletedDate(e.target.value)} data-testid="pm-completion-date" /></div>
              <div><label className="text-xs text-gray-500">Completed By *</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="Technician name" value={completedBy} onChange={e => setCompletedBy(e.target.value)} data-testid="pm-completed-by" /></div>
            </div>
            <div><label className="text-xs text-gray-500">Reason for Delay *</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="e.g. Spare part not available" value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="pm-remarks" /></div>
          </div>
        )}
        {status === "CANCELLED" && (
          <div className="col-span-2 space-y-3 bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-xs font-semibold text-red-700">Cancellation Details</p>
            <div><label className="text-xs text-gray-500">Reason for Cancellation *</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="e.g. Machine scrapped" value={remarks} onChange={e => setRemarks(e.target.value)} data-testid="pm-remarks" /></div>
            <div><label className="text-xs text-gray-500">Cancelled By</label><input className="w-full border rounded-lg px-2 py-1.5 text-sm mt-1" placeholder="Your name" value={completedBy} onChange={e => setCompletedBy(e.target.value)} data-testid="pm-completed-by" /></div>
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={handleSave} disabled={saving} data-testid="pm-save-status" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{saving ? "Saving..." : "Save Status"}</button>
        <button onClick={onClose} data-testid="pm-cancel-status" className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
        {pmCellStatus(currentEntry) && can(user, "clearPMCell") && (
          <button onClick={() => onSave(null)} data-testid="pm-clear-status" className="ml-auto border border-red-300 text-red-500 px-4 py-2 rounded-lg text-sm hover:bg-red-50">Clear Cell</button>
        )}
      </div>
      {showCheckSheetForm && matchedTemplate && (
        <PMCheckSheetFillModal
          key={matchedTemplate.id || effectiveSheetFreq}
          template={matchedTemplate}
          plan={plan}
          month={month}
          pmHalf={pmHalf}
          existingData={savedSheetFitsTemplate ? checkSheetData : null}
          completedDate={completedDate}
          completedBy={completedBy}
          machine={matchedMachine}
          sheetFreqOptions={sheetFreqOptions}
          onChangeSheetFreq={changeSheetFreq}
          onSave={(data) => { setCheckSheetData(data); setShowCheckSheetForm(false); }}
          onClose={() => setShowCheckSheetForm(false)}
        />
      )}
    </Modal>
  );
}

function PMCell({ entry, canEdit, unitId, planId, month }) {
  const status = pmCellStatus(entry);
  const meta = pmCellMeta(entry);
  if (!status) return <span className="text-gray-200">&ndash;</span>;

  async function downloadEvidence(e) {
    e.stopPropagation();
    // Prefer R2 signed URL if cell has evidencePhotoKey (the path after Step 4 backfill).
    if (meta.evidencePhotoKey && unitId && planId && month) {
      try {
        const result = await apiCall("GET", `/pm-plans/${unitId}/${planId}/evidence/${encodeURIComponent(month)}/url`);
        if (result && result.url) {
          window.open(result.url, "_blank");
          return;
        }
      } catch (err) {
        alert("Failed to load evidence: " + (err.message || err));
        return;
      }
    }
    // Fallback to base64 data URL if R2 key is missing (defensive — shouldn't happen post-backfill).
    if (meta.evidenceFile) {
      const a = document.createElement("a");
      a.href = meta.evidenceFile;
      a.download = meta.evidenceFileName || "evidence";
      a.click();
    }
  }

  const isExternalDone = status === "DONE" && (meta.workType || "").toLowerCase() === "external";
  const tooltipWho = isExternalDone ? (meta.vendorName || "Vendor") : meta.completedBy;
  // A planned PM whose month has already gone by \u2014 still due, carried forward.
  const overdue = isPMCellOverdue(month, status);
  return (
    <div className="relative group inline-flex items-center gap-0.5">
      <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${canEdit ? "cursor-pointer" : ""} ${overdue ? SC_OVERDUE : SC[status]}`}
        title={overdue ? `Overdue \u2014 was due ${month}, still not completed` : undefined}
        data-testid={overdue ? `pm-overdue-${planId}-${month}` : undefined}>
        {status === "PLAN" ? "\u25AC" : status === "DONE" ? "\u2713" : status === "DELAYED" ? "!" : "\u2715"}
      </span>
      {(meta.evidencePhotoKey || meta.evidenceFile) && (
        <span className="text-blue-500 cursor-pointer hover:text-blue-700 text-xs" onClick={downloadEvidence} title={meta.evidenceFileName || "Download evidence"} data-testid="pm-evidence-icon">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 inline" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd"/></svg>
        </span>
      )}
      {isExternalDone && (
        <span className="px-1 py-0 rounded text-[9px] font-bold bg-orange-100 text-orange-700 border border-orange-300 leading-tight" data-testid="pm-ext-tag" title={meta.vendorName ? `External \u2014 ${meta.vendorName}` : "External"}>EXT</span>
      )}
      {(status === "DONE" || status === "DELAYED") && tooltipWho && (
        <div className="hidden group-hover:block absolute z-30 bottom-full left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs rounded px-2 py-1 whitespace-nowrap mb-1 pointer-events-none" data-testid="pm-tooltip">
          {tooltipWho}{isExternalDone && meta.poNumber ? ` \u00B7 PO ${meta.poNumber}` : ""} &middot; {meta.completedDate}{meta.remarks ? ` \u00B7 ${meta.remarks}` : ""}
        </div>
      )}
    </div>
  );
}

export default function PMPlan({ pmPlan, setPmPlan, user, unitId, refreshPM, machines = [], floors = [], pmCheckSheetTemplates = [], initialSearch = "" }) {
  // Arriving from a PM Health row, already searched down to the machine it named. Seeded
  // through the mount key, so coming back a second time for a different machine re-applies
  // it rather than leaving the first search in place.
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [floorFilter, setFloorFilter] = useState("ALL");
  const [locationFilter, setLocationFilter] = useState("ALL");
  const [freqFilter, setFreqFilter] = useState("ALL");
  const [statusModal, setStatusModal] = useState(null);
  const [editPlanModal, setEditPlanModal] = useState(null);
  const [fyFilter, setFyFilter] = useState("2025-26");
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedPrintMonth, setSelectedPrintMonth] = useState("");
  const [printingMonth, setPrintingMonth] = useState(false);
  const pmGridRef = useRef(null);

  const availableFYs = useMemo(() => { const fys = new Set(pmPlan.map(p => p.fy || "2025-26")); return [...fys].sort(); }, [pmPlan]);
  useEffect(() => { if (availableFYs.length > 0) setFyFilter(availableFYs[availableFYs.length - 1]); }, [availableFYs.length]);
  const nextFY = useMemo(() => { const latest = availableFYs[availableFYs.length - 1] || "2025-26"; const yr = parseInt(latest.split("-")[0]) + 1; return `${yr}-${String(yr + 1).slice(-2)}`; }, [availableFYs]);
  const isPastFY = useMemo(() => { const fyStartYear = parseInt(fyFilter.split("-")[0]); return new Date(fyStartYear + 1, 3, 1) <= new Date(); }, [fyFilter]);
  const activeMonths = useMemo(() => getMonthsForFY(fyFilter), [fyFilter]);
  // Floors configured for this unit in Settings, falling back to the built-in list.
  const unitFloors = useMemo(() => {
    const names = (floors || []).map(f => f.name).filter(Boolean);
    return names.length ? names : FLOORS;
  }, [floors]);
  const floorFilterOptions = ["ALL", ...unitFloors];
  const frequencies = ["ALL", "6 MONTH", "1 YEAR", "NO SCHEDULE"];
  const fyPlan = useMemo(() => pmPlan.filter(p => (p.fy || "2025-26") === fyFilter), [pmPlan, fyFilter]);
  const filteredLocations = useMemo(() => { const locs = fyPlan.filter(p => floorFilter === "ALL" || p.floor === floorFilter).map(p => p.location); return ["ALL", ...[...new Set(locs)].sort()]; }, [fyPlan, floorFilter]);
  const allFloorsForEdit = useMemo(() => [...new Set(fyPlan.map(p => p.floor).filter(Boolean))].sort(), [fyPlan]);
  const allLocationsForEdit = useMemo(() => [...new Set(fyPlan.map(p => p.location).filter(Boolean))].sort(), [fyPlan]);
  function handleFloorChange(val) { setFloorFilter(val); setLocationFilter("ALL"); }
  const filteredPM = useMemo(() => { const q = searchQuery.toLowerCase(); return fyPlan.filter(p => { const matchSearch = !q || p.desc.toLowerCase().includes(q) || p.machineCode.toLowerCase().includes(q); const matchFloor = floorFilter === "ALL" || p.floor === floorFilter; const matchLocation = locationFilter === "ALL" || p.location === locationFilter; const matchFreq = freqFilter === "ALL" || p.freq === freqFilter; return matchSearch && matchFloor && matchLocation && matchFreq; }); }, [fyPlan, searchQuery, floorFilter, locationFilter, freqFilter]);

  function openStatusModal(plan, month) { if (!can(user, "editPM")) return; setStatusModal({ plan, month, entry: plan.schedule[month] }); }

  async function saveStatus(planId, month, data) {
    const plan = pmPlan.find(p => p.id === planId);
    if (!plan) return;
    const newSchedule = { ...plan.schedule };
    if (data === null) { delete newSchedule[month]; }
    else {
      const entry = { status: data.status };
      if (data.completedDate) entry.completedDate = data.completedDate;
      if (data.completedBy) entry.completedBy = data.completedBy;
      if (data.remarks) entry.remarks = data.remarks;
      if (Array.isArray(data.evidenceFiles)) { entry.evidenceFiles = data.evidenceFiles; }
      else if (data.evidenceFile) { entry.evidenceFile = data.evidenceFile; entry.evidenceFileName = data.evidenceFileName; }
      if (data.checkSheetData) entry.checkSheetData = data.checkSheetData;
      // Internal / External hybrid fields. Only attach when the modal sent them (status = DONE).
      if (data.workType) entry.workType = data.workType;
      if (data.vendorName) entry.vendorName = data.vendorName;
      if (data.poNumber) entry.poNumber = data.poNumber;
      if (data.poDate) entry.poDate = data.poDate;
      if (data.poAmount !== undefined && data.poAmount !== null && data.poAmount !== "") entry.poAmount = data.poAmount;
      if (data.poFilePath) entry.poFilePath = data.poFilePath;
      if (data.poFileName) entry.poFileName = data.poFileName;
      if (data.paymentMode) entry.paymentMode = data.paymentMode;
      // Internal-only time tracking. Empty strings are intentional (External clears them on save).
      if (data.startDate !== undefined) entry.startDate = data.startDate;
      if (data.startTime !== undefined) entry.startTime = data.startTime;
      if (data.endDate !== undefined) entry.endDate = data.endDate;
      if (data.endTime !== undefined) entry.endTime = data.endTime;
      newSchedule[month] = entry;
    }
    try { await apiCall("PUT", `/pm-plans/${unitId}/${planId}`, { schedule: newSchedule }); await refreshPM(); setStatusModal(null); }
    catch (e) { alert("Failed to update PM status: " + (e?.response?.data?.detail || e?.message || "Unknown error")); }
  }

  async function saveEditedPlan(planId, payload) {
    try {
      await apiCall("PUT", `/pm-plans/${unitId}/${planId}/edit-full`, payload);
      await refreshPM();
      setEditPlanModal(null);
    } catch (e) {
      alert("Failed to save edit: " + (e?.response?.data?.detail || e?.message || "Unknown error"));
    }
  }

  async function handleDeletePlan(plan) {
    const code = plan.machineCode || plan.id;
    const fy = plan.fy || "2025-26";
    if (!window.confirm(`Delete PM Plan for ${code} (FY ${fy})?\n\nThis will permanently remove this row from PM Plan.\n\nDONE marks already logged in Maintenance Log will NOT be affected.\n\nCannot be undone \u2014 use Restore PM Plans backup if you need to recover.`)) return;
    try {
      await apiCall("DELETE", `/pm-plans/${unitId}/${plan.id}`);
      await refreshPM();
    }
    catch (e) { alert("Failed to delete PM Plan: " + (e?.response?.data?.detail || e?.message || "Unknown error")); }
  }

  async function handleAutoGenerate() {
    const targetFY = fyFilter;
    const now = new Date();
    const currentFYStart = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    const targetStart = parseInt(targetFY.split("-")[0]);
    if (targetStart < currentFYStart) {
      alert(`Cannot generate plans for past financial year FY ${targetFY}.`);
      return;
    }
    const isCurrentFY = (targetStart === currentFYStart);
    const priorYearStart = targetStart - 1;
    const priorFY = `${priorYearStart}-${String(targetStart).slice(-2)}`;
    let action, note;
    if (isCurrentFY) {
      action = "Re-Generate";
      note = `\n\nThis will:\n- Pull location and floor from Machine List\n- Auto-correct schedule dots to match frequency (1 YEAR = 1 dot, 6 MONTH = 2 dots 6 apart, NO SCHEDULE = 0)\n- NOT change frequency labels (edit those manually)\n- NOT change DONE marks\n- NOT touch manually-edited rows\n- Skip machines that have no FY ${targetFY} plan`;
    } else {
      action = "Auto-Generate";
      note = `\n\nThis will:\n- Create new plans based on last PM completion date\n- Press machines: 6-month frequency\n- Other machines: 1-year frequency\n- Skip machines that already have a FY ${targetFY} plan\n- Skip manually-edited plans`;
    }
    if (!window.confirm(`${action} PM Plan for FY ${targetFY}?${note}\n\nProceed?`)) return;
    setGenerating(true);
    try {
      const result = await apiCall("POST", `/pm-plans/${unitId}/auto-generate`, { fy: targetFY });
      setGenResult(result);
      await refreshPM();
      setFyFilter(targetFY);
    }
    catch { alert(`Failed to ${action.toLowerCase()} PM plan`); }
    finally { setGenerating(false); }
  }

  async function handleExportPDF() {
    setExportingPDF(true);
    await new Promise(r => setTimeout(r, 300));
    const el = pmGridRef.current;
    if (!el) { setExportingPDF(false); return; }
    const opt = {
      margin: [5, 3, 5, 3],
      filename: `PM-Plan_FY${fyFilter}_${unitId}_${new Date().toISOString().split('T')[0]}.pdf`,
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 1.2, useCORS: true, logging: false, width: el.scrollWidth, windowWidth: el.scrollWidth },
      jsPDF: { unit: 'mm', format: 'a3', orientation: 'landscape' }
    };
    try { await html2pdf().set(opt).from(el).save(); }
    catch (err) { console.error("PM Plan PDF export error:", err); alert("Failed to export PDF."); }
    finally { setExportingPDF(false); }
  }

  async function handlePrintMonthPDF() {
    if (!selectedPrintMonth) { alert("Please select a month"); return; }
    setPrintingMonth(true);
    try {
      // Due list = the selected month's PMs plus anything still outstanding from earlier
      // months of the same FY, carried forward until it is completed. When printing a
      // FUTURE month, only genuinely-overdue months (already past today) are carried in —
      // a PM planned for next month is not late yet.
      const _curMonthDate = monthKeyToDate(currentMonthKey());
      const pending = getDuePMs(fyPlan, selectedPrintMonth, fyFilter)
        .filter(d => !d.carriedForward || (_curMonthDate && d.monthDate < _curMonthDate));
      const carriedCount = pending.filter(d => d.carriedForward).length;
      const monthPlans = fyPlan.filter(p => p.schedule[selectedPrintMonth] !== undefined);
      const completed = monthPlans.filter(p => {
        const s = typeof p.schedule[selectedPrintMonth] === "object" ? p.schedule[selectedPrintMonth].status : p.schedule[selectedPrintMonth];
        return s === "DONE" || s === "DELAYED";
      });

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2,"0")}-${String(now.getMonth()+1).padStart(2,"0")}-${now.getFullYear()}`;
      const th = 'style="border:1px solid #999;padding:6px 10px;font-size:11px;background:#e8e8e8;font-weight:bold;text-align:left"';
      const td = 'style="border:1px solid #ccc;padding:6px 10px;font-size:11px"';
      const tdC = 'style="border:1px solid #ccc;padding:6px 10px;font-size:11px;text-align:center"';

      let html = `<div style="font-family:Arial,sans-serif;padding:10px">`;
      html += `<div style="text-align:center;margin-bottom:16px">`;
      html += `<div style="font-size:18px;font-weight:bold">ADVANCED ANMOL METCOMP PVT. LTD.</div>`;
      html += `<div style="font-size:14px;font-weight:bold;color:#333;margin-top:4px">Monthly PM Schedule \u2014 ${escapeHtml(selectedPrintMonth)}</div>`;
      html += `<div style="font-size:10px;color:#888;margin-top:4px">FY ${escapeHtml(fyFilter)} | Generated: ${dateStr} | Document: MNT-F-03</div>`;
      html += `</div>`;

      if (pending.length > 0) {
        html += `<div style="font-size:12px;font-weight:bold;margin-bottom:6px;color:#333">Machines Due for PM \u2014 ${escapeHtml(selectedPrintMonth)} (${pending.length})`;
        if (carriedCount > 0) {
          html += `<span style="font-weight:normal;color:#b91c1c"> \u00b7 includes ${carriedCount} carried forward from earlier months</span>`;
        }
        html += `</div>`;
        html += `<table style="border-collapse:collapse;width:100%;margin-bottom:16px"><thead><tr>`;
        ["#","Machine Name","Machine Code","Location","Frequency","Planned Month","\u2713 Done"].forEach(h => { html += `<th ${th}>${h}</th>`; });
        html += `</tr></thead><tbody>`;
        pending.forEach((d, i) => {
          const p = d.plan;
          const rowBg = d.carriedForward ? ' style="background:#fef2f2"' : "";
          html += `<tr${rowBg}><td ${tdC}>${i+1}</td><td ${td} style="border:1px solid #ccc;padding:6px 10px;font-size:11px;font-weight:600">${escapeHtml(p.desc)}</td>`;
          html += `<td ${td} style="border:1px solid #ccc;padding:6px 10px;font-size:10px;font-family:monospace;color:#2563eb">${escapeHtml(p.machineCode)}</td>`;
          html += `<td ${td}>${escapeHtml(p.location)}</td><td ${tdC}>${escapeHtml(p.freq)}</td>`;
          html += d.carriedForward
            ? `<td ${tdC} style="border:1px solid #ccc;padding:6px 10px;font-size:10px;color:#b91c1c;font-weight:bold">${escapeHtml(d.month)}<br/>OVERDUE ${d.monthsLate}M</td>`
            : `<td ${tdC} style="border:1px solid #ccc;padding:6px 10px;font-size:10px">${escapeHtml(d.month)}</td>`;
          html += `<td ${tdC} style="border:1px solid #ccc;padding:6px 10px;font-size:16px;width:50px">\u2610</td></tr>`;
        });
        html += `</tbody></table>`;
      } else {
        html += `<div style="text-align:center;color:#999;padding:20px;font-size:12px">No pending PMs for ${selectedPrintMonth}</div>`;
      }

      if (completed.length > 0) {
        html += `<div style="font-size:11px;font-weight:bold;margin-bottom:4px;color:#16a34a;margin-top:12px">Already Completed (${completed.length})</div>`;
        html += `<table style="border-collapse:collapse;width:100%;margin-bottom:16px"><thead><tr>`;
        ["#","Machine Name","Machine Code","Status","Completed By","Date"].forEach(h => { html += `<th ${th}>${h}</th>`; });
        html += `</tr></thead><tbody>`;
        completed.forEach((p, i) => {
          const entry = typeof p.schedule[selectedPrintMonth] === "object" ? p.schedule[selectedPrintMonth] : {};
          html += `<tr style="background:#f0fdf4"><td ${tdC}>${i+1}</td><td ${td}>${escapeHtml(p.desc)}</td>`;
          html += `<td ${td} style="border:1px solid #ccc;padding:6px 10px;font-size:10px;font-family:monospace">${escapeHtml(p.machineCode)}</td>`;
          html += `<td ${tdC}><span style="color:${entry.status === "DONE" ? "#16a34a" : "#d97706"};font-weight:bold">${escapeHtml(entry.status)}</span></td>`;
          html += `<td ${td}>${escapeHtml(entry.completedBy)}</td><td ${td}>${escapeHtml(entry.completedDate)}</td></tr>`;
        });
        html += `</tbody></table>`;
      }

      html += `<div style="margin-top:30px;display:flex;justify-content:space-between;font-size:10px;color:#666">`;
      html += `<div style="border-top:1px solid #ccc;padding-top:4px;width:30%;text-align:center">Prepared By (Plant Head)</div>`;
      html += `<div style="border-top:1px solid #ccc;padding-top:4px;width:30%;text-align:center">Received By (Maintenance)</div>`;
      html += `<div style="border-top:1px solid #ccc;padding-top:4px;width:30%;text-align:center">Verified By</div>`;
      html += `</div></div>`;

      const div = document.createElement("div");
      div.innerHTML = html;
      document.body.appendChild(div);
      const opt = { margin: 8, filename: `PM_Monthly_${selectedPrintMonth}_${unitId}.pdf`, image: { type: "jpeg", quality: 0.98 }, html2canvas: { scale: 2, useCORS: true }, jsPDF: { unit: "mm", format: "a4", orientation: "portrait" } };
      await html2pdf().set(opt).from(div).save();
      document.body.removeChild(div);
      setShowMonthPicker(false);
    } catch (error) {
      alert("PDF export failed: " + (error.message || "Unknown error"));
    } finally {
      setPrintingMonth(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="pm-plan">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-gray-800">Preventive Maintenance Plan &middot; FY {fyFilter}</h2>
          <p className="text-xs text-gray-500">MNT-F-03{can(user, "editPM") ? " \u00B7 Click a cell to update status" : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-500 font-medium">Financial Year</label>
            <select className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white font-semibold" value={fyFilter} onChange={e => setFyFilter(e.target.value)} data-testid="fy-select">
              {availableFYs.map(fy => <option key={fy} value={fy}>FY {fy}</option>)}
              {!availableFYs.includes(nextFY) && <option key={nextFY} value={nextFY}>FY {nextFY} (new)</option>}
            </select>
          </div>
          <button onClick={handleExportPDF} disabled={exportingPDF} className="border border-red-500 text-red-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-50 disabled:opacity-50 whitespace-nowrap" data-testid="pm-export-pdf-btn">
            {exportingPDF ? "Exporting..." : "Export PDF"}
          </button>
          <button onClick={() => { setSelectedPrintMonth(activeMonths[0] || ""); setShowMonthPicker(true); }} className="border border-blue-500 text-blue-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-50 whitespace-nowrap" data-testid="pm-print-month-btn">
            Print Month
          </button>
          {user.role === "superadmin" && (() => {
            const now = new Date();
            const currentFYStart = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1;
            const targetStart = parseInt(fyFilter.split("-")[0]);
            if (targetStart < currentFYStart) return null;
            const isCurrentFY = (targetStart === currentFYStart);
            const label = isCurrentFY ? `Re-Generate FY ${fyFilter}` : `Auto-Generate FY ${fyFilter}`;
            return (
              <button onClick={handleAutoGenerate} disabled={generating} data-testid="auto-generate-pm"
                className="bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-700 disabled:opacity-50 whitespace-nowrap">
                {generating ? "Generating..." : label}
              </button>
            );
          })()}
          {user.role === "superadmin" && (
            <>
              <input
                type="file"
                accept=".json"
                style={{ display: "none" }}
                id="pm-restore-file-input"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  if (!window.confirm(`Restore PM plans for the current unit from this backup file?\n\nThis will REPLACE all current PM plans for this unit with the data in the backup file. Other units and other data will not be affected.\n\nA pre-restore backup is created automatically.\n\nProceed?`)) {
                    e.target.value = "";
                    return;
                  }
                  try {
                    const formData = new FormData();
                    formData.append("file", file);
                    const response = await axios.post(`${API}/backup/restore-unit-pm-plans/${unitId}`, formData, {
                      headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
                    });
                    alert(`Restore complete!\n\nReplaced: ${response.data.deleted_count} old plans\nRestored: ${response.data.restored_count} plans from backup\n\nPre-restore backup saved as: ${response.data.pre_restore_backup}\n\nPlease refresh the page.`);
                    await refreshPM();
                  } catch (err) {
                    alert("Restore failed: " + (err.response?.data?.detail || err.message));
                  }
                  e.target.value = "";
                }}
              />
              <button
                onClick={() => document.getElementById("pm-restore-file-input").click()}
                className="border border-purple-500 text-purple-600 px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-purple-50 whitespace-nowrap">
                Restore PM Plans
              </button>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-3 flex-wrap text-xs">
        {Object.entries(SL).map(([k, v]) => <span key={k} className={`px-2 py-0.5 rounded font-semibold ${SC[k]}`}>{v}</span>)}
        <span className={`px-2 py-0.5 rounded font-semibold ${SC_OVERDUE}`} title="Planned month has passed and the PM is still not completed — it stays on the due list until it is done">Overdue</span>
        {!can(user, "editPM") && <span className="text-gray-400">&middot; Read-only for your role</span>}
      </div>
      <div className="flex flex-wrap gap-3 items-end" data-testid="pm-filters">
        <div className="flex-1 min-w-48">
          <label className="text-xs text-gray-500 font-medium block mb-1">Search Machine</label>
          <input className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Name or machine code..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-testid="pm-search" />
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Floor</label>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white w-40" value={floorFilter} onChange={e => handleFloorChange(e.target.value)} data-testid="pm-floor-filter">{floorFilterOptions.map(f => <option key={f}>{f}</option>)}</select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">Location / Section</label>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white min-w-56" value={locationFilter} onChange={e => setLocationFilter(e.target.value)} data-testid="pm-location-filter">{filteredLocations.map(l => <option key={l}>{l}</option>)}</select>
        </div>
        <div>
          <label className="text-xs text-gray-500 font-medium block mb-1">PM Frequency</label>
          <select className="border rounded-lg px-3 py-2 text-sm bg-white" value={freqFilter} onChange={e => setFreqFilter(e.target.value)} data-testid="pm-freq-filter">{frequencies.map(f => <option key={f}>{f}</option>)}</select>
        </div>
        {(searchQuery || floorFilter !== "ALL" || locationFilter !== "ALL" || freqFilter !== "ALL") && (
          <button onClick={() => { setSearchQuery(""); setFloorFilter("ALL"); setLocationFilter("ALL"); setFreqFilter("ALL"); }} className="border border-red-300 text-red-500 px-3 py-2 rounded-lg text-sm hover:bg-red-50 mb-0.5" data-testid="pm-clear-filters">Clear</button>
        )}
      </div>
      <div className="text-xs text-gray-400">Showing {filteredPM.length} of {fyPlan.length} machines &middot; FY {fyFilter}</div>

      <div className="bg-white rounded-xl border overflow-hidden" ref={pmGridRef}>
        {/* The header row stays put while the grid scrolls. During PDF export the
            height cap is lifted, otherwise html2canvas would capture only the rows
            currently on screen. */}
        <div className={exportingPDF ? "overflow-x-auto" : "overflow-auto max-h-[70vh]"}>
          <table className="w-full text-xs min-w-[900px]" data-testid="pm-plan-table">
            <thead>
              <tr className="bg-gray-50 border-b">
                {["#", "Description", "Code", "Location", "Freq."].map(h => <th key={h} className="text-left px-3 py-2 text-gray-500 font-semibold sticky top-0 z-20 bg-gray-50">{h}</th>)}
                {activeMonths.map(m => <th key={m} className="px-2 py-2 text-gray-500 font-semibold text-center whitespace-nowrap sticky top-0 z-20 bg-gray-50">{m}</th>)}
              </tr>
            </thead>
            <tbody>
              {filteredPM.map((p, i) => (
                <tr key={p.id} className={`border-b hover:bg-gray-50 ${p.autoGenerated ? "bg-green-50/30" : ""}`}>
                  <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                  <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">
                    {p.desc}
                    {p.autoGenerated && <span className="ml-1.5 px-1.5 py-0.5 bg-green-100 text-green-700 rounded text-[10px] font-semibold" title={`Generated by ${p.generatedBy}`}>AUTO</span>}
                    {/* The machine has left the floor. The row stays so the PM actually
                        carried out this year is still on the plan, but nothing further
                        falls due — the future months were cleared when it was retired. */}
                    {p.retired && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${String(p.retired.status).toUpperCase().includes("SOLD") ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}
                        title={`${p.retired.status} on ${p.retired.date} — no further PM is scheduled`}
                        data-testid={`pm-retired-${p.machineCode}`}>
                        {String(p.retired.status).toUpperCase()}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-blue-600 whitespace-nowrap text-xs">{p.machineCode}</td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{p.location}</td>
                  <td className="px-3 py-2 text-gray-500">{p.freq}</td>
                  {activeMonths.map(m => (
                    <td key={m} className={`px-1 py-2 text-center ${can(user, "editPM") ? "cursor-pointer" : ""}`}
                      onClick={() => openStatusModal(p, m)} data-testid={`pm-cell-${p.id}-${m}`}>
                      <PMCell entry={p.schedule[m]} canEdit={can(user, "editPM")} unitId={unitId} planId={p.id} month={m} />
                    </td>
                  ))}
                {(can(user, "editPMPlanRow") || can(user, "deletePMPlanRow")) && (
                  <td className="px-2 py-1 text-right whitespace-nowrap">
                    {can(user, "editPMPlanRow") && (
                      <button
                        onClick={() => setEditPlanModal(p)}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline mr-3"
                        data-testid={`edit-plan-${p.id}`}>
                        Edit
                      </button>
                    )}
                    {can(user, "deletePMPlanRow") && (
                      <button
                        onClick={() => handleDeletePlan(p)}
                        className="text-xs text-red-600 hover:text-red-800 hover:underline"
                        data-testid={`delete-plan-${p.id}`}>
                        Delete
                      </button>
                    )}
                  </td>
                )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {statusModal && (
        <PMStatusModal plan={statusModal.plan} month={statusModal.month} currentEntry={statusModal.entry} machines={machines} pmCheckSheetTemplates={pmCheckSheetTemplates} user={user}
          onSave={(data) => saveStatus(statusModal.plan.id, statusModal.month, data)} onClose={() => setStatusModal(null)} />
      )}

      {editPlanModal && (
        <PMEditModal
          plan={editPlanModal}
          fyMonths={activeMonths}
          floorOptions={allFloorsForEdit}
          unitFloors={unitFloors}
          locationOptions={allLocationsForEdit}
          onSave={(payload) => saveEditedPlan(editPlanModal.id, payload)}
          onClose={() => setEditPlanModal(null)}
        />
      )}

      {genResult && (
        <Modal title={genResult.mode === "auto_generate" ? "Auto-Generate Complete" : "Re-Generate Complete"} onClose={() => setGenResult(null)}>
          <div className="space-y-4">
            {genResult.mode === "regenerate" ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-700">{genResult.updated || 0}</p>
                  <p className="text-xs text-green-600 font-medium">Plans Synced</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-blue-700">{genResult.auto_corrected || 0}</p>
                  <p className="text-xs text-blue-600 font-medium">Schedules Auto-Corrected</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-700">{genResult.skipped_edited || 0}</p>
                  <p className="text-xs text-yellow-600 font-medium">Skipped (Manually Edited)</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-600">{genResult.skipped_no_plan || 0}</p>
                  <p className="text-xs text-gray-500 font-medium">Skipped (No 26-27 Plan)</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-green-700">{genResult.generated || 0}</p>
                  <p className="text-xs text-green-600 font-medium">New Plans Created</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-gray-600">{genResult.skipped_existing || 0}</p>
                  <p className="text-xs text-gray-500 font-medium">Skipped (Already Has Plan)</p>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-700">{genResult.skipped_edited || 0}</p>
                  <p className="text-xs text-yellow-600 font-medium">Skipped (Manually Edited)</p>
                </div>
              </div>
            )}
            <p className="text-xs text-gray-500 text-center">
              {genResult.mode === "regenerate"
                ? "Pulled location/floor from Machine List. Auto-corrected dot patterns to match frequency. DONE marks preserved. Frequency label not changed."
                : "Schedule based on last PM completion date and machine frequency defaults."}
            </p>
            <button onClick={() => setGenResult(null)} className="w-full bg-blue-600 text-white py-2 rounded-lg text-sm font-semibold hover:bg-blue-700" data-testid="close-gen-result">Close</button>
          </div>
        </Modal>
      )}

      {showMonthPicker && (
        <Modal title="Print Monthly PM List" onClose={() => setShowMonthPicker(false)}>
          <div className="space-y-4">
            <p className="text-xs text-gray-500">Select a month to generate a printable PM task sheet with a checkbox column for the maintenance team.</p>
            <div>
              <label className="text-xs text-gray-500 font-medium block mb-1">Select Month</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white font-semibold" value={selectedPrintMonth} onChange={e => setSelectedPrintMonth(e.target.value)} data-testid="print-month-select">
                {activeMonths.map(m => {
                  const count = fyPlan.filter(p => p.schedule[m] !== undefined).length;
                  return <option key={m} value={m}>{m} ({count} machines)</option>;
                })}
              </select>
            </div>
            {selectedPrintMonth && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs">
                <div className="font-semibold text-blue-800 mb-1">Preview for {selectedPrintMonth}:</div>
                <div className="text-blue-600">
                  {(() => {
                    const _cm = monthKeyToDate(currentMonthKey());
                    const due = getDuePMs(fyPlan, selectedPrintMonth, fyFilter)
                      .filter(d => !d.carriedForward || (_cm && d.monthDate < _cm));
                    const carried = due.filter(d => d.carriedForward).length;
                    const mp = fyPlan.filter(p => p.schedule[selectedPrintMonth] !== undefined);
                    const done = mp.filter(p => { const s = pmCellStatus(p.schedule[selectedPrintMonth]); return s === "DONE" || s === "DELAYED"; }).length;
                    return `${due.length} due (${carried} carried forward) \u00B7 ${done} completed`;
                  })()}
                </div>
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={handlePrintMonthPDF} disabled={printingMonth || !selectedPrintMonth} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50" data-testid="generate-month-pdf-btn">
                {printingMonth ? "Generating..." : "Generate PDF"}
              </button>
              <button onClick={() => setShowMonthPicker(false)} className="border border-gray-300 text-gray-600 px-4 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
